import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AuthError, type SignInChallenge, type SignInOutcome } from '@volee/auth'
import { toast } from '@volee/ui'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  applyAuthFailure,
  applyProblems,
  formErrorOf,
  mfaProblems,
  newPasswordProblems,
  signInProblems,
  MFA_FIELDS,
  NEW_PASSWORD_FIELDS,
  SIGN_IN_FIELDS,
  type MfaFields,
  type NewPasswordFields,
  type SignInFields,
} from './auth-fields.ts'
import { signInEmailOf } from './sign-in-identifier.ts'
import { loadMe, ME_KEY } from '../auth/me.ts'
import { authErrorMessage, messages } from '../messages/index.ts'
import { auth } from '../services/auth.ts'

// Signing in with a password, and the first access that follows a temporary one. The first
// access is a STATE of /sign-in and not a route: it cannot be re-entered on its own.

// The order matters: the anonymous answer has to leave the cache before the guard of the
// destination reads it, or it bounces the just-signed-in user straight back here.
export function useSessionLanding(
  options: { readonly adding?: boolean } = {},
): (returnTo: string) => Promise<void> {
  const cache = useQueryClient()
  const navigate = useNavigate()
  const adding = options.adding === true

  return useCallback(
    async (returnTo: string) => {
      // An account added beside another: nothing the other read may stay (#302).
      if (adding) cache.clear()
      cache.removeQueries({ queryKey: ME_KEY })
      await loadMe(cache)
      // Awaited: the guard runs in `beforeLoad`, and it reads what loadMe just put in. Replaced
      // and not pushed: the sign-in page is not a place to go back to from the destination.
      await navigate({ to: returnTo, replace: true })
    },
    [cache, navigate, adding],
  )
}

/** The two stops a sign-in can make. Derived, so a third challenge cannot be left unhandled. */
type Challenged = Exclude<SignInOutcome, { kind: 'signed-in' }>

export function useSignIn(returnTo: string, options: { readonly adding?: boolean } = {}) {
  const land = useSessionLanding(options)
  const [pending, setPending] = useState<Challenged | null>(null)
  // Set when the challenge dies: the credentials form comes back carrying the explanation,
  // because "start again from the sign-in page" is unreadable while standing on a dead one.
  const [restart, setRestart] = useState<string>()
  const credentialsForm = useForm<SignInFields>({ defaultValues: { email: '', password: '' } })
  const passwordForm = useForm<NewPasswordFields>({
    defaultValues: { newPassword: '', confirmPassword: '' },
  })
  const mfaForm = useForm<MfaFields>({ defaultValues: { code: '' } })

  // Cognito's session dies on its own clock; spending a request to be told so is waste.
  function liveChallenge(): SignInChallenge {
    if (pending === null || pending.challenge.expiresAt <= Date.now()) {
      throw new AuthError('challenge_expired', 'The challenge is gone or past its deadline')
    }
    return pending.challenge
  }

  /** A dead challenge sends the credentials form back, carrying the explanation. The two
   *  forms are emptied with it: what was typed belonged to that challenge, and the next one
   *  would receive it under an autofocused cursor. */
  function restartedOn(error: unknown): boolean {
    if (!(error instanceof AuthError) || error.code !== 'challenge_expired') return false
    setPending(null)
    passwordForm.reset()
    mfaForm.reset()
    setRestart(authErrorMessage(error))
    return true
  }

  const signIn = useMutation({
    // Trimmed, like every other flow: a pasted address with a trailing space would come back
    // as the one message that deliberately explains nothing. A username is a child's (#294).
    mutationFn: (fields: SignInFields) =>
      auth.password.signIn({ email: signInEmailOf(fields.email), password: fields.password }),
    onSuccess: async (outcome) => {
      if (outcome.kind !== 'signed-in') {
        setPending(outcome)
        return
      }
      await land(returnTo)
    },
    onError: (error: unknown) => {
      applyAuthFailure(credentialsForm.setError, SIGN_IN_FIELDS, error)
    },
  })

  const completeFirstAccess = useMutation({
    mutationFn: (fields: NewPasswordFields) =>
      auth.password.completeNewPassword(liveChallenge(), fields.newPassword),
    onSuccess: async () => {
      // The destination shows a page, not the change that was made: the toast is what says it.
      toast.success(messages.newPassword.saved)
      await land(returnTo)
    },
    onError: (error: unknown) => {
      if (restartedOn(error)) return
      applyAuthFailure(passwordForm.setError, NEW_PASSWORD_FIELDS, error)
    },
  })

  const completeMfa = useMutation({
    mutationFn: (fields: MfaFields) =>
      auth.password.completeMfa(liveChallenge(), fields.code.trim()),
    onSuccess: () => land(returnTo),
    onError: (error: unknown) => {
      if (restartedOn(error)) return
      applyAuthFailure(mfaForm.setError, MFA_FIELDS, error)
    },
  })

  function submitCredentials(fields: SignInFields) {
    credentialsForm.clearErrors()
    setRestart(undefined)
    const problems = signInProblems(fields, messages.password.required)
    if (applyProblems(credentialsForm.setError, problems)) return
    signIn.mutate(fields)
  }

  function submitNewPassword(fields: NewPasswordFields) {
    passwordForm.clearErrors()
    if (applyProblems(passwordForm.setError, newPasswordProblems(fields, messages.password))) return
    completeFirstAccess.mutate(fields)
  }

  function submitMfa(fields: MfaFields) {
    mfaForm.clearErrors()
    if (applyProblems(mfaForm.setError, mfaProblems(fields, messages.password.required))) return
    completeMfa.mutate(fields)
  }

  return {
    pending,
    credentials: {
      form: credentialsForm,
      submit: submitCredentials,
      pending: signIn.isPending,
      formError: restart ?? formErrorOf(SIGN_IN_FIELDS, signIn.error),
    },
    newPassword: {
      form: passwordForm,
      submit: submitNewPassword,
      pending: completeFirstAccess.isPending,
      formError: formErrorOf(NEW_PASSWORD_FIELDS, completeFirstAccess.error),
    },
    mfa: {
      form: mfaForm,
      submit: submitMfa,
      pending: completeMfa.isPending,
      formError: formErrorOf(MFA_FIELDS, completeMfa.error),
    },
  }
}
